import { Box, Skeleton, Stack, Typography, useTheme } from "@mui/material";
import { useIntl } from "react-intl";

import { useGetEarnings } from "api";
import { Widget } from "components/elements/mui";
import { useFormatMessage } from "lang";
import { EARNINGS } from "routes/consts";
import { priceSeriesColors } from "styles/theme";

import { formatPln } from "./format";

const Part = ({
  label,
  value,
  color,
}: {
  label: string;
  value: number | null;
  color: string;
}) => {
  const intl = useIntl();
  return (
    <Stack alignItems="flex-end" minWidth={0}>
      <Typography
        variant="caption"
        fontWeight={600}
        letterSpacing={0.5}
        sx={{ color }}
      >
        {label}
      </Typography>
      <Typography
        variant="body1"
        fontWeight={700}
        lineHeight={1.2}
        color={value !== null && value < 0 ? "error.main" : "text.primary"}
      >
        {formatPln(intl, value)} PLN
      </Typography>
    </Stack>
  );
};

// What today's production has earned so far under the Tradea contract, split
// into what the day-ahead sale brings (RDN) and what the imbalance
// settlement adds or takes (PSE). Hidden for plants without Tradea
// credentials.
export const EarningsWidget = ({
  disableLink = false,
}: {
  disableLink?: boolean;
}) => {
  const formatMessage = useFormatMessage();
  const intl = useIntl();
  const theme = useTheme();
  const { data, isLoading } = useGetEarnings();
  const { pse: pseColor } = priceSeriesColors(theme.palette.mode);
  const rdnColor = theme.palette.primary.main;

  if (!isLoading && (!data || !data.configured)) return null;

  const total = data?.configured ? data.totals.totalPln : 0;
  const status = data?.configured
    ? data.settled
      ? formatMessage("earnings.settled")
      : formatMessage("earnings.estimate")
    : "";

  return (
    <Widget
      size={{ xs: 12 }}
      title={formatMessage("earnings.today")}
      url={disableLink ? undefined : EARNINGS}
      content={
        isLoading || !data?.configured ? (
          <Box py={0.5}>
            <Skeleton variant="text" width={140} height={40} animation="wave" />
            <Skeleton variant="text" width={200} animation="wave" />
          </Box>
        ) : (
          <Box py={0.5}>
            <Stack
              direction="row"
              alignItems="flex-end"
              justifyContent="space-between"
              gap={2}
            >
              <Stack minWidth={0}>
                <Stack direction="row" alignItems="baseline" gap={0.75}>
                  <Typography
                    variant="h4"
                    fontWeight={700}
                    lineHeight={1}
                    color={total < 0 ? "error.main" : "text.primary"}
                  >
                    {formatPln(intl, total)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    PLN
                  </Typography>
                </Stack>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: 11 }}
                >
                  {formatMessage("earnings.soFar")} · {status}
                </Typography>
              </Stack>
              {/* A fixed-price plant has no day-ahead / imbalance split to
                  show — the whole day is production × the contract rate. */}
              {data.mode !== "fixed" && (
                <Stack direction="row" gap={2}>
                  <Part
                    label={formatMessage("prices.rdn")}
                    value={data.totals.rdnPln}
                    color={rdnColor}
                  />
                  <Part
                    label={formatMessage("prices.pse")}
                    value={data.totals.psePln}
                    color={pseColor}
                  />
                </Stack>
              )}
            </Stack>
          </Box>
        )
      }
    />
  );
};
